import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter } from "@/components/ui/card"

import {
    Plus,
    CalendarClock,
    Package,
    QrCode,
    Download,
    Pencil,
    AlertTriangle,
    Clock,
    History,
    Eye,
    EyeOff,
    KeyRound,
} from "lucide-react"
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import { parseAdditionalEquipment, parseCredentials } from "@/Wist-blockchain/collections/instruments/types"
import { EquipmentImageGallery } from "../../components/EquipmentImageGallery "
import { getInstrumentQrUrl } from "@/Wist-blockchain/collections/instruments/instruments-storage.service"
import { Label } from "@/components/ui/label"
import { Link, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { CertificationHistoryCard } from "../../components/CertificationHistoryCard"
import { AuditLogCard } from "../../components/AuditLogCard"
import { DraftBanner } from "../../../serviceReport/components/draftBanner"




// --- Credenciales ---

function CredentialsSection({ creds }: { creds: { username: string; password: string }[] }) {
    const [visibleIdx, setVisibleIdx] = useState<number[]>([])
    const toggle = (i: number) =>
        setVisibleIdx(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])

    return (
        <div className="w-full mt-1">
            <Label className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Credenciales
            </Label>
            <div className="mt-2 w-full space-y-2">
                {creds.map((cred, i) => (
                    <div key={i} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-muted-foreground">Usuario</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200 break-all">{cred.username || "—"}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">Contraseña</span>
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-700 dark:text-gray-200 break-all">
                                    {visibleIdx.includes(i) ? cred.password || "—" : "••••••••"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => toggle(i)}
                                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                                >
                                    {visibleIdx.includes(i)
                                        ? <EyeOff className="h-3.5 w-3.5" />
                                        : <Eye className="h-3.5 w-3.5" />
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// --- Componente principal ---

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    operativo:               { label: "Operativo",                    className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    operativo_observaciones: { label: "Operativo con observaciones",  className: "bg-amber-100 text-amber-700 border-amber-200" },
    fuera_servicio:          { label: "Fuera de servicio",            className: "bg-red-100 text-red-600 border-red-200" },
}

const EquipmentDetail = ({ equipment, brandName, orgName, nextMaintenanceDate }: {
    equipment: Instrument
    brandName: string | null
    orgName: string | null
    nextMaintenanceDate: string | null
}) => {

    const navigate = useNavigate()
    const [showAuditSheet, setShowAuditSheet] = useState(false)
    const statusCfg = STATUS_CONFIG[equipment.status ?? ""] ?? { label: equipment.status ?? "—", className: "" }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <Package className="h-4 w-4" />
                        <BreadcrumbLink asChild>
                            <Link to="/instruments/listInstruments">Lista de Instrumentos</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage> {equipment.equipmentName} </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>


            <div className="space-y-6 pt-6">

                {/* Header: título + botón */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-2xl font-bold  sm:text-3xl">
                            {equipment.equipmentName}
                        </h1>
                        <span
                            className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold ${statusCfg.className}`}
                        >
                            {statusCfg.label}
                        </span>
                    </div>
                    <Button className="cursor-pointer" onClick={() => {
                        sessionStorage.removeItem(`draft_${equipment.$id}`)
                        navigate({
                            to: '/instruments/serviceReport/$id',
                            params: { id: equipment.$id },
                            search: { draftId: undefined },
                        })
                    }}>
                        <Plus className="h-4 w-4" />
                        Nuevo Informe
                    </Button>
                </div>

                {/* ID del sistema */}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Identificador único del sistema:{" "}
                    <span className="font-semibold text-blue-600 dark:text-blue-500">{equipment.serialNumber}</span>
                </p>

                {/* Contenido principal: 2 columnas en desktop */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

                    {/* Columna izquierda */}
                    <div className="space-y-5 lg:col-span-1">

                        {/* Imagen del equipo */}
                        <div>
                            <EquipmentImageGallery
                                idFiles={equipment.idFiles ?? []}
                                equipmentName={equipment.equipmentName ?? ""}
                            />
                        </div>
                        {/* Información del equipo */}
                        <Card className="border-gray-200">
                            <CardContent className="pt-5">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Información del Equipo
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => setShowAuditSheet(true)}
                                            className="h-7 gap-1.5 text-xs cursor-pointer bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:text-gray-800 shadow-none"
                                        >
                                            <History className="h-3.5 w-3.5" />
                                            Historial
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => navigate({ to: '/instruments/$id/edit', params: { id: equipment.$id } })}
                                            className="h-7 gap-1.5 text-xs cursor-pointer bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:text-amber-800 shadow-none"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                            Editar
                                        </Button>
                                    </div>
                                </div>

                                <dl className="space-y-2.5 text-sm">
                                    {[
                                        { label: "Nombre", value: equipment.equipmentName },
                                        { label: "S/N", value: equipment.serialNumber },
                                        { label: "Código activo del cliente", value: equipment.clientAssetCode },

                                        { label: "Organización o cliente", value: orgName ?? "—" },
                                        { label: "Modelo", value: equipment.model },
                                        { label: "Fabricante", value: brandName ?? "—" },
                                        { label: "Año", value: equipment.manufactureYear },
                                        { label: "Voltaje", value: equipment.voltage },
                                        { label: "Corriente", value: equipment.current },
                                        { label: "Versión de Software", value: equipment.softwareVersion },
                                        { label: "Estado", value: statusCfg.label },
                                        { label: "Ubicación", value: equipment.location },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="flex justify-between gap-2">
                                            <dt className="text-gray-500">{label}</dt>
                                            <dd className="text-gray-700 dark:text-gray-400 text-right">{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </CardContent>
                            <CardFooter className="flex-col items-start gap-2 border-t px-5 py-4">
                                <Label className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                                    Descripción
                                </Label>
                                <p className="w-full rounded-md bg-gray-50 p-3 text-sm leading-relaxed text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                    {equipment.equipmentDescription || "Sin descripción disponible."}
                                </p>

                                {/* Credenciales */}
                                {(equipment.credentials ?? []).length > 0 && (() => {
                                    const creds = parseCredentials(equipment.credentials ?? [])
                                    return (
                                        <CredentialsSection creds={creds} />
                                    )
                                })()}

                                {/* Equipamiento adicional */}
                                {equipment.additionalEquipment?.length > 0 && (
                                    <div className="w-full mt-1">
                                        <Label className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                                            Equipamiento Adicional
                                        </Label>
                                        <div className="mt-2 w-full space-y-2">
                                            {parseAdditionalEquipment(equipment.additionalEquipment).map((item, index) => (
                                                <div
                                                    key={index}
                                                    className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <span className="font-medium text-gray-700 dark:text-gray-200 wrap-break-word min-w-0">
                                                            {item.nombre || `Equipo ${index + 1}`}
                                                        </span>
                                                        {item.estado && (
                                                            <span className="text-xs text-muted-foreground shrink-0">{item.estado}</span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                                                        {item.serial && <p className="break-all">S/N: {item.serial}</p>}
                                                        {item.modelo && <p className="wrap-break-word">Modelo: {item.modelo}</p>}
                                                        {item.descripcion && <p className="wrap-break-word">{item.descripcion}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardFooter>
                        </Card>

                        {/* QR Card */}
                        <Card className="border border-dashed border-blue-200 bg-blue-50/40">
                            <CardContent className="flex flex-col items-center gap-4 py-6">

                                <div className="flex h-36 w-36 items-center justify-center rounded-xl bg-gray-900 overflow-hidden shadow-lg">
                                    {equipment.idQrFile ? (
                                        <img
                                            src={getInstrumentQrUrl(equipment.idQrFile)}
                                            alt="QR del equipo"
                                            className="h-full w-full object-contain p-2"
                                        />
                                    ) : (
                                        <QrCode className="h-20 w-20 text-white opacity-80" />
                                    )}
                                </div>

                                <div className="text-center">
                                    <p className="font-semibold text-gray-800">Identificador QR Único</p>
                                    <p className="text-xs text-gray-500">Escanee para verificar trazabilidad en sitio</p>
                                </div>

                                {equipment.idQrFile && (
                                    <div className="flex gap-2">
                                        <a href={getInstrumentQrUrl(equipment.idQrFile)} download={`QR-${equipment.serialNumber}.png`} target="_blank" rel="noreferrer">
                                            <Button variant="outline" size="sm" className="gap-1.5 text-xs cursor-pointer">
                                                <Download className="h-3.5 w-3.5" />
                                                Descargar
                                            </Button>
                                        </a>
                                        <a href={`https://wistrace.wist-ec.com/equipo/${equipment.$id}`} target="_blank" rel="noreferrer">
                                            <Button size="sm" className="gap-1.5 bg-blue-600 text-xs hover:bg-blue-700 text-white cursor-pointer">
                                                <QrCode className="h-3.5 w-3.5" />
                                                Wistrace
                                            </Button>
                                        </a>
                                    </div>
                                )}

                            </CardContent>
                        </Card>

                    </div>

                    {/* Columna derecha */}
                    <div className="space-y-5 lg:col-span-2">
                        {/* Historial de calificaciones */}
                        <DraftBanner instrumentId={equipment.$id} />
                        <CertificationHistoryCard instrumentId={equipment.$id} />

                        {/* Siguiente recalificación programada */}
                        {(() => {
                            if (!nextMaintenanceDate) return null

                            const date = new Date(`${nextMaintenanceDate.split("T")[0]}T12:00:00`)
                            const today = new Date()
                            today.setHours(0, 0, 0, 0)
                            const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                            const formatted = date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })

                            const cfg = diffDays < 0
                                ? {
                                    card: "border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30",
                                    icon: "bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400",
                                    title: "text-red-800 dark:text-red-300",
                                    sub: "text-red-600 dark:text-red-400",
                                    badge: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300",
                                    label: "Mantenimiento vencido",
                                    Icon: AlertTriangle,
                                    detail: `Venció el ${formatted}`,
                                }
                                : diffDays <= 30
                                    ? {
                                        card: "border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30",
                                        icon: "bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400",
                                        title: "text-amber-800 dark:text-amber-300",
                                        sub: "text-amber-600 dark:text-amber-400",
                                        badge: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300",
                                        label: "Vence pronto",
                                        Icon: Clock,
                                        detail: `${formatted} · en ${diffDays} día${diffDays === 1 ? "" : "s"}`,
                                    }
                                    : {
                                        card: "border-blue-100 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20",
                                        icon: "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400",
                                        title: "text-gray-800 dark:text-blue-300",
                                        sub: "text-gray-500 dark:text-blue-400",
                                        badge: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300",
                                        label: "Programado",
                                        Icon: CalendarClock,
                                        detail: `${formatted} · en ${diffDays} días`,
                                    }

                            return (
                                <Card className={`border ${cfg.card}`}>
                                    <CardContent className="flex items-center gap-3 py-4">
                                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${cfg.icon}`}>
                                            <cfg.Icon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className={`text-sm font-semibold ${cfg.title}`}>
                                                    Siguiente Recalificación
                                                </p>
                                                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cfg.badge}`}>
                                                    {cfg.label}
                                                </span>
                                            </div>
                                            <p className={`text-xs mt-0.5 ${cfg.sub}`}>{cfg.detail}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            )
                        })()}

                    </div>
                </div>



            </div >

            <Sheet open={showAuditSheet} onOpenChange={setShowAuditSheet}>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                    <SheetHeader className="mb-4">
                        <SheetTitle className="flex items-center gap-2">
                            <History className="h-4 w-4 text-muted-foreground" />
                            Historial de cambios
                        </SheetTitle>
                        <SheetDescription>
                            Registro de todas las modificaciones realizadas a este instrumento.
                        </SheetDescription>
                    </SheetHeader>
                    <AuditLogCard instrumentId={equipment.$id} />
                </SheetContent>
            </Sheet>
        </>
    )
}

export default EquipmentDetail